import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { useState } from 'react';
import { useSearchParams } from "react-router-dom";
import Baguette from '../../components/baguette/Baguette';
import RecipeList from '../../components/recipes/RecipeList';
import AdvancedSearch from '../../components/search/AdvancedSearch';
// import RecipeListSimple from '../../components/recipes/RecipeListSimple';
import Tags, { Tag } from '../../components/search/Tags';
import Info from '../../components/settings/Info';
import Settings from '../../components/settings/Settings';
import * as constants from "../../store/Constants";
import { Recipe } from '../../store/Recipe';
import { filterIndex } from '../../utils/Filter';
import { getLocalStorage } from '../../utils/LocalStorageHelpers';
import { getAllOptions, updateQuery } from '../../utils/QueryHelpers';
import { sortRecipeByDate, sortRecipeByName, sortRecipeByTime, SortType } from '../../utils/Sort';
import { getRecipeIndex, SyncState } from '../../utils/ServerCommunications';

function Main() {
  const currentYear = new Date().getFullYear();

  // State will consist of the search parameters and the recipe index.
  let [searchParams, setSearchParams] = useSearchParams();

  // Use localStorage available if possible.
  const localStorageResult = getLocalStorage();
  const [recipeIndex, setRecipeIndex] = useState<Recipe[]>(localStorageResult.index);

  // The filteredIndex is the result of the combination of the above.
  var filteredIndex = filterIndex({ query: searchParams, index: recipeIndex });

  // All possible tags from index (but from a specific set of columns).
  const tags = getAllOptions({ index: filteredIndex }).map(option => { return { label: option } })

  // Parse URL for keyword query.
  const currentKeywords = searchParams.getAll(constants.Keyword).map((name) => {
    return {
      label: name
    } as Tag
  })

  // Helper function for setting URL with new keyword query.
  function setKeywordQuery(tags: Tag[]) {
    updateQuery({
      column: constants.Keyword,
      query: searchParams,
      tags: tags,
      setQuery: setSearchParams
    })
  }

  // Handle show/hide of advanced search.
  const [showAdvancedSearch, setShowAdvancedSearch] = React.useState(false);
  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch((prev) => !prev);
  };

  const [showSettings, setShowSettings] = React.useState(false);
  const toggleShowSettings = () => {
    setShowSettings((prev) => !prev);
  };

  const [showInfo, setShowInfo] = React.useState(false);
  const toggleShowInfo = () => {
    setShowInfo((prev) => !prev);
  };

  const [sort, setSort] = React.useState<SortType>(SortType.DATE);
  const handleSort = (
    event: React.MouseEvent<HTMLElement>,
    newSort: SortType,
  ) => {
    setSort(newSort);
  };

  if (sort === SortType.ALPHABETICAL) {
    filteredIndex = filteredIndex.sort(sortRecipeByName)
  } else if (sort === SortType.TIME) {
    filteredIndex = filteredIndex.sort(sortRecipeByTime)
  } else if (sort === SortType.DATE) {
    filteredIndex = filteredIndex.sort(sortRecipeByDate) 
    filteredIndex.reverse()
  } else {
    // No sort required. Instead use the spreadsheet order on last sync.
    // But reverse it so the "lastest" recipe is at the top.
    filteredIndex.reverse()
  }

  // Local state for icon.
  const [syncIconState, setSyncIconState] = React.useState(SyncState.COMPLETE)
  function setSyncIconHelper(state: SyncState) {
    setSyncIconState(state)
  }

  const isSyncing = (syncIconState === SyncState.IN_PROGRES)

  React.useEffect(() => {
    getRecipeIndex({
      url: constants.DefaultUrl,
      setData: (index: Recipe[]) => { setRecipeIndex(index) },
      setStatus: setSyncIconHelper
    })
  }, [recipeIndex.length]);

  return (
    <Container>
      <Baguette />
      <Settings
        state={showSettings}
        setState={setShowSettings}
        setRecipeIndex={setRecipeIndex}
      />
      <Info
        state={showInfo}
        setState={toggleShowInfo}
      />
      <div>
        <div>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <IconButton onClick={toggleShowInfo}>
              <InfoIcon />
            </IconButton>
            <h3>Search the MAK Protocols</h3>
            <IconButton onClick={toggleShowSettings}>
              <SettingsIcon />
            </IconButton>
          </Stack>
          <Tags
            label={"Recipe Name or Keyword"}
            options={tags}
            value={currentKeywords}
            setValue={setKeywordQuery}
          />
        </div>
        <div>
          <FormControlLabel
            control={<Switch checked={showAdvancedSearch} onChange={toggleAdvancedSearch} />}
            label="Show Advanced Search"
          />
          <Collapse in={showAdvancedSearch}>
            <AdvancedSearch
              index={recipeIndex}
              filteredIndex={filteredIndex}
              query={searchParams}
              setQuery={setSearchParams}
            />
          </Collapse>
        </div>
        <Box sx={{ padding: '10px' }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <p> Showing {filteredIndex.length}/{recipeIndex.length} recipes below. </p>
            {isSyncing &&
              <p>Syncing...</p>
            }
            <ToggleButtonGroup
              value={sort}
              exclusive
              onChange={handleSort}
              aria-label="recipe sort"
            >
              <ToggleButton value={SortType.DATE} aria-label="sort by date (or position in list)">
                <CalendarMonthIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value={SortType.ALPHABETICAL} aria-label="sort by alphabetical">
                <SortByAlphaIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value={SortType.TIME} aria-label="sort by time">
                <TimelapseIcon fontSize='small' />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {recipeIndex.length === 0 &&
            <Stack direction="row" alignItems="center" >
              {isSyncing &&
                <p>Syncing with server. Please wait.</p>
              }
            </Stack>
          }
          <RecipeList
            index={filteredIndex}
          />
        </Box>
        <Box
          sx={{ "textAlign": "center", padding: '10px' }}
        >
          <hr />
          <p>&copy; Karl Parks {currentYear}</p>
        </Box>
      </div>
    </Container>
  )
}

export default Main